import React from 'react';
import {
  DeadpanItemId,
  DeadpanItemRarity,
  GamePhase,
  PlayerRole,
  TimerPhase,
  getLanguage,
} from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

const NORMAL_CARD_IMAGE = new URL('../../../../../resources/normal.png', import.meta.url).href;
const RARE_CARD_IMAGE = new URL('../../../../../resources/rare.png', import.meta.url).href;
const EPIC_CARD_IMAGE = new URL('../../../../../resources/epic.png', import.meta.url).href;

const HINT_ITEM_IDS = new Set<DeadpanItemId>([
  DeadpanItemId.CATEGORY_HINT,
  DeadpanItemId.COINFLIP_CATEGORY,
  DeadpanItemId.USELESS_HINT,
]);

function getCardImage(rarity?: DeadpanItemRarity): string {
  if (rarity === DeadpanItemRarity.RARE) return RARE_CARD_IMAGE;
  if (rarity === DeadpanItemRarity.EPIC) return EPIC_CARD_IMAGE;
  return NORMAL_CARD_IMAGE;
}

export const DeadpanItemPanel: React.FC = () => {
  const { socket, currentRoom, gameState, myPlayerId } = useSocket();
  const isZh = getLanguage() === 'zh';
  const t = useTranslations();
  const itemT = t.games.deadpan.items;
  const [expanded, setExpanded] = React.useState(false);

  if (!socket || !currentRoom || currentRoom.gameType !== 'deadpan' || !myPlayerId) {
    return null;
  }

  const me = currentRoom.players.find((player) => player.id === myPlayerId);
  const slot = me?.deadpanItemSlot;
  const hint = me?.deadpanHint;
  const itemsEnabled = currentRoom.deadpanConfig?.itemsEnabled ?? false;

  if (!slot) {
    return null;
  }

  const emitUse = () => {
    socket.emit('client:use-deadpan-item');
  };

  const getAvailabilityMessage = () => {
    if (!slot || !itemsEnabled) return null;
    if (currentRoom.phase === GamePhase.WAITING || currentRoom.phase === GamePhase.RESULTS) {
      return itemT.panelUnavailable;
    }

    if (HINT_ITEM_IDS.has(slot.itemId) && me?.role !== PlayerRole.DEADPAN) {
      return itemT.panelRoleUnavailable;
    }

    if (slot.itemId === DeadpanItemId.PREP_MINUS_FIVE || slot.itemId === DeadpanItemId.PREP_PLUS_FIVE) {
      return currentRoom.phase === GamePhase.ROLE_ASSIGNMENT ? null : itemT.panelUnavailable;
    }

    if (slot.itemId === DeadpanItemId.EXPLANATION_MINUS_FIVE) {
      return currentRoom.phase === GamePhase.ROLE_ASSIGNMENT && gameState?.timer?.phase === TimerPhase.EXPLANATION_DISPLAY
        ? null
        : itemT.panelUnavailable;
    }

    if (HINT_ITEM_IDS.has(slot.itemId)) {
      return currentRoom.phase === GamePhase.ROLE_ASSIGNMENT ? null : itemT.panelUnavailable;
    }

    if (slot.itemId === DeadpanItemId.REORDER_SPEAKERS) {
      return currentRoom.phase === GamePhase.VOTING ? null : itemT.panelUnavailable;
    }

    if (slot.itemId === DeadpanItemId.BIG_TIME_CUT) {
      return currentRoom.phase === GamePhase.ROLE_ASSIGNMENT ? null : itemT.panelUnavailable;
    }

    return itemT.panelUnavailable;
  };

  const availabilityMessage = getAvailabilityMessage();
  const isCurrentlyUsable = itemsEnabled && !availabilityMessage;

  const renderUseButton = () => {
    if (!slot || !isCurrentlyUsable) {
      return null;
    }

    const buttonClassName = slot.itemId === DeadpanItemId.BIG_TIME_CUT
      ? 'px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white font-semibold hover:from-fuchsia-700 hover:to-pink-700'
      : 'px-3 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:from-indigo-700 hover:to-purple-700';

    return (
      <button
        type="button"
        onClick={() => emitUse()}
        className={buttonClassName}
      >
        {itemT.panelUse}
      </button>
    );
  };

  const cardBody = (
    <>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full rounded-2xl border-2 border-transparent bg-transparent p-3 shadow-none transition-all"
      >
        <img
          src={getCardImage(slot?.rarity)}
          alt={slot ? itemT.cardTitles[slot.itemId] : 'item card'}
          className="mx-auto h-36 w-auto object-contain"
        />
        <p className="mt-3 text-sm font-semibold text-gray-800">
          {itemsEnabled ? itemT.cardTitles[slot.itemId] : itemT.panelDisabled}
        </p>
        {itemsEnabled && availabilityMessage && (
          <p className="mt-1 text-xs font-medium text-gray-500">{availabilityMessage}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">{expanded ? itemT.panelCollapse : itemT.panelExpand}</p>
      </button>

      {expanded && slot && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => socket.emit('client:toggle-deadpan-item-lock', !slot.locked)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                slot.locked
                  ? 'border-amber-300 bg-amber-100 text-amber-800'
                  : 'border-slate-300 bg-slate-100 text-slate-700'
              }`}
            >
              {slot.locked ? itemT.panelLocked : itemT.panelUnlocked}
            </button>
            {renderUseButton()}
          </div>
        </div>
      )}

      {expanded && hint && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs font-semibold text-emerald-800 mb-1">{itemT.panelYourHint}</p>
          <p className="text-sm sm:text-base font-medium text-emerald-900">{isZh ? hint.textZh : hint.text}</p>
        </div>
      )}
    </>
  );

  return (
    <div className="w-full max-w-[256px] rounded-2xl border-2 border-transparent bg-transparent p-3 sm:p-4">
      {cardBody}
    </div>
  );
};
